import React from "react";
import { createPortal } from "react-dom";
import { useFloatingCoords } from "../../../hooks/useFloatingCoords";
import type { ExistingWorkCode } from "../../../models";
import { swatchHex } from "../../../utils/color";
import WorkCodeField from "../../shared/WorkCodeField/WorkCodeField";
import "./AddWorkCodePopover.css";

interface Props {
  scheduleId: number;
  /** Codes already on this section's palette — pre-listed in the picker. */
  extraCodes: ExistingWorkCode[];
  /** Add the picked / newly-created code to the palette. */
  onAdd: (code: string, name: string, color: string) => void;
  onClose: () => void;
}

/** "+ Add work code" — the same picker used everywhere else (search existing
 *  codes across the schedule, or create a new one), floated under the button.
 *  Rendered into document.body so the strip's own overflow/stacking never
 *  clips it. Adding a code closes the popover (Esc / click-out also close).
 *  Codes already on the palette are excluded from suggestions. */
const AddWorkCodePopover: React.FC<Props> = ({
  scheduleId,
  extraCodes,
  onAdd,
  onClose,
}) => {
  const [buf, setBuf] = React.useState({ code: "", name: "", color: "" });
  // Codes already on the palette are never suggested — the picker's job is
  // codes you DON'T have yet.
  const onPalette = React.useMemo(
    () => new Set(extraCodes.map((c) => c.code)),
    [extraCodes],
  );
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const popRef = React.useRef<HTMLDivElement>(null);
  const coords = useFloatingCoords(anchorRef, true, popRef);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if ((t as HTMLElement).closest?.(".work-code-field-pop")) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <span ref={anchorRef} className="add-code-pop-anchor" aria-hidden="true">
      {coords &&
        createPortal(
          <div
            className="add-code-pop"
            ref={popRef}
            style={{ top: coords.top, left: coords.left }}
          >
            <WorkCodeField
              inline
              scheduleId={scheduleId}
              code={buf.code}
              name={buf.name}
              color={buf.color}
              onChange={(p) => setBuf((b) => ({ ...b, ...p }))}
              extraCodes={extraCodes}
              excludeCodes={onPalette}
              footer={
                <button
                  type="button"
                  className="add-code-pop-apply"
                  disabled={!buf.code.trim()}
                  onClick={() => {
                    const code = buf.code.trim();
                    if (!code) return;
                    onAdd(code, buf.name.trim() || code, swatchHex(buf.color, code));
                    onClose();
                  }}
                >
                  Add{buf.code.trim() ? ` ${buf.code.trim()}` : ""} to the palette
                </button>
              }
            />
          </div>,
          document.body,
        )}
    </span>
  );
};

export default AddWorkCodePopover;
